<script lang="ts">
	import { createEventDispatcher } from 'svelte'
	import { fade } from 'svelte/transition'
	import { circOut } from 'svelte/easing'

	import { XIcon, HeartIcon, PlusIcon } from '@rgossiaux/svelte-heroicons/outline'
	import {
		HeartIcon as HeartIconSolid,
		BookmarkIcon as BookmarkIconSolid
	} from '@rgossiaux/svelte-heroicons/solid'
	import { createPopperActions } from 'svelte-popperjs'
	import dayjs from 'dayjs'
	import { notifications, notification_bookmarks } from '$lib/stores/notifications'
	import { longpress } from '$lib/actions/longpress'
	import type Book from '$lib/types/book'
	import { clickOutside } from '$lib/actions/clickOutside'
	import { popup } from '$lib/utils/toast'
	import * as userData from '$lib/stores/userData'
	import { BookSize } from '$lib/utils/constants'
	import { likes, bookmarks } from '$lib/stores/userData'

	import Rating from '$lib/components/Rating.svelte'
	import type { JournalEntry } from '$lib/types/entry'
	
	import { getBookImage, getHighestResImage, hasBookImage } from '$lib/types/book'
	import {  user } from '$lib/stores/userData'

	const [popperRef, popperContent] = createPopperActions({
		placement: 'bottom'
	})
	const extraOpts = {
		modifiers: [
			{ name: 'offset', options: { offset: [0, 8] } },
			{
				name: 'flip',
				options: {
					allowedAutoPlacements: ['right', 'left', 'top', 'bottom'],
					rootBoundary: 'viewport'
				}
			}
		]
	}
	const dispatch = createEventDispatcher()

	// API
	export let book: Book

	export let size: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' = 'md'
	export let maxHeight: number = BookSize[size].height
	export let maxWidth: number = BookSize[size].width

	// Display options: showDetail, showStars, showDate, showLikes, showAdd, showRemove
	export let showDetail = false
	export let detail: 'title' | 'pages' = 'title'

	export let showStars = false
	export let stars = 0

	export let showDate: boolean = false
	export let date: string | null | undefined = undefined

	export let showLikes = true

	export let selectable = true

	// new UI
	export let showAdd = false 
	export let showRemove = false 
	export let showRemoveOnHover = false

	$: largeBook = maxWidth > 120

	let entry: JournalEntry | undefined = undefined

	const addLike = async (book: Book) => {
		if (!$user) return

		$likes = await userData.addLike($likes, book?.uuid)
		if (book.likes) book.likes++
		else book.likes = 1
	}
	const removeLike = async (book: Book) => {
		if (!$user) return

		$likes = await userData.removeLike($likes, book.uuid)

		if (book.likes) book.likes--
		else book.likes = 0
	}

	const addBookmark = async () => {
		if (!$user) return

		console.log('adding bookmark')

		$bookmarks = await userData.addBookmark($bookmarks, book.uuid)

		$notification_bookmarks++

		dispatch('addedBookmark', book)

		popup('Saved')
	}
	const removeBookmark = async () => {
		if (!$user) return

		$bookmarks = await userData.removeBookmark($bookmarks, book.uuid)

		dispatch('removedBookmark', book)
	}

	let showJournalSelectModal = false

	const onAdd = async () => {
		dispatch('add')
		
	}
	const onRemove = async () => {
		dispatch('remove')

	}

	let menuPopup = false
	const onLongPress = (e: CustomEvent) => (menuPopup = true)

	const handleMenuItem = (item: string) => {
		menuPopup = false

		switch (item) {
			case 'save':
				addBookmark()
				break
			case 'remove':
				removeBookmark()
				break
			case 'addToJournal':
				showJournalSelectModal = true
				break
			case 'removeFromJournal':
				dispatch('remove')
				break
			case 'edit':
				break
		}
	}

	const onScroll = () => (menuPopup = false)

	function getCoverClass(book: Book) {
		const coverClass = [
			{
				background: 'bg-gradient-to-br from-app to-orange-300',
				titleText: 'text-white uppercase overflow-hidden line-clamp-4 drop-shadow-xl font-semibold',
				authorText: 'text-black text-sm font-bold'
			},
			{
				background: 'bg-gradient-to-br from-teal-500 to-blue-500',
				titleText: 'text-white uppercase overflow-hidden line-clamp-4 drop-shadow-xl font-semibold',
				authorText: 'text-black text-sm font-bold'
			},
			{
				background: 'bg-gradient-to-l from-yellow-900 to-yellow-300',
				titleText:
					'text-white uppercase overflow-hidden line-clamp-4 drop-shadow-xl font-bold tracking-tight',
				authorText: 'text-white text-sm drop-shadow-xl uppercase font-smibold tracking-tight'
			}
		]
		let index = 0
		if (book && book.author && book.title)
			index = (book.author?.length + book.title?.length) % coverClass.length
		if (index < 0) index = 0
		if (index > 2) index = 2
		return coverClass[index]
	}
</script>

<svelte:window on:scroll={onScroll} />
{#if book}
<div class="flex flex-col justify-center items-center">
	<button
		class="{menuPopup ? 'z-40' : ''}
			rounded-md bg-gray-700 {selectable ? 'cursor-pointer' : 'cursor-default'} shadow-lg select-none"
		style="max-height: {maxHeight}px; min-height: {maxHeight}px; max-width: {maxWidth}px; min-width: {maxWidth}px"
		use:longpress={500}
		use:popperRef
		on:contextmenu|preventDefault|stopPropagation
		on:click={() => dispatch('click', book)}
		on:longpress={onLongPress}
	>
		<div class="group relative">
			{#if hasBookImage(book)}
				<img
					src={largeBook ? getHighestResImage(book) : getBookImage(book, 'md')}
					alt={book.title ?? ""}
					style="max-height: {maxHeight}px; min-height: {maxHeight}px; max-width: {maxWidth}px; min-width: {maxWidth}px"
					class=" object-fit w-full rounded-md {selectable ? 'hover:brightness-50' : ''}"
					loading="lazy"
				/>
			{:else}
				<div
					style="min-height: {maxHeight}px; max-height: {maxHeight}"
					class="w-full h-full max-h-[20px] p-2 hover:brightness-50 rounded-lg flex flex-col text-center justify-start items-center overflow-hidden
						{getCoverClass(book).background}"
				>
					<div class="{getCoverClass(book).titleText} overflow-hidden">
						{book.title ?? ""}
					</div>
					<div class="grow" />
				</div>
			{/if}

			{#if showRemove}
				<div class="absolute bottom-2 right-2 menu-button bg-gray-200 text-chili
					{showRemoveOnHover ? 'hidden-till-hover hidden-on-mobile' : ''}
				">
					<button on:click|stopPropagation|preventDefault={onRemove}>
						<XIcon class="h-7" />
					</button>
				</div>
			{/if}

			{#if showAdd}
				<div class="absolute bottom-2 right-2 menu-button-transparent bg-gray-200">
					<button class="" on:click|stopPropagation|preventDefault={onAdd}>
						<PlusIcon class="h-7" />
					</button>
				</div>
			{/if}

			{#if book.uuid && userData.isBookmarked($bookmarks, book.uuid)}
				<button
					class="absolute z-10 -top-3 -right-3 rounded-full bg-white border shadow-md flex text-green-500 text-xs items-center hover:scale-110 duration-300"
					on:keydown={() => removeBookmark()}
					on:click|stopPropagation|preventDefault={() => removeBookmark()}
					aria-roledescription="button"
				>
					<BookmarkIconSolid class="h-6" />
			</button>
			{:else if book.uuid}
				<button
					class="{menuPopup
						? 'z-40'
						: ''} absolute -top-3 -right-3 hidden-till-hover hidden-on-mobile rounded-full bg-white border shadow-md flex text-gray-500 text-xs items-center hover:scale-110 duration-300"
					on:keydown={() => addBookmark()}
					on:click|stopPropagation|preventDefault={() => addBookmark()}
				>
					<BookmarkIconSolid class="h-6" />
					</button>
			{/if}
		</div>
	</button>
	<div
		class="flex w-full items-center overflow-hidden"
		style="max-width: {maxWidth}px; min-width: {maxWidth}px"
	>
		{#if showDetail && largeBook}
			<div class="-mt-1 shrink flex-col items-start justify-start -space-y-0.5">
				<div
					style:font-size="0.9rem"
					class="font-bold tracking-tight line-clamp-1 text-left dark:text-gray-200 text-gray-500 mt-1.5"
				>
					{#if detail === 'title'}
						{book.title ?? ""}
					{:else if detail === 'pages' && book.publishedDate}
						Published {new Date(book.publishedDate).getFullYear()}
					{/if}
				</div>
				<div
					style:font-size="0.7rem"
					class="font-medium text-left line-clamp-1 tracking-tight dark:text-gray-100 text-gray-500"
				>
					{#if detail === 'title'}
						{#if book.author}
							{book.author}
						{/if}
					{:else if detail === 'pages' && book.pages}
						{book.pages} pages
					{/if}
				</div>
			</div>
		{:else if showStars}
			<div class="p-0.5 cursor-none">
				<Rating bind:value={stars} fontSize={'0.9rem'} editable={false} />
			</div>
		{:else if showDate && date && date !== ''}
			<div class="p-0.5 select-none text-sm">
				{dayjs(date).format('MMM D')}
			</div>
		{/if}

		{#if showLikes}
			<div class="grow" />
			{#if userData.isLiked($likes, book.uuid)}
				<button
					on:click|stopPropagation|preventDefault={() => removeLike(book)}
					class="cursor-pointer shrink-0 flex items-center justify-start text-xs hover:scale-110 duration-300 hover:text-chili"
				>
					<HeartIconSolid class="text-chili {largeBook ? 'h-8' : 'h-6'}" />
					{#if book.likes && book.likes > 0}
						<div class="">{book.likes}</div>
					{/if}
				</button>
			{:else}
				<button
					on:click|stopPropagation|preventDefault={() => addLike(book)}
					class="cursor-pointer flex items-center justify-center text-xs text-gray-500 hover:scale-110 duration-300 hover:text-black"
				>
					{#if book.likes && book.likes > 0}
						<HeartIconSolid class={largeBook ? 'h-8' : 'h-6'} />
						<div>{book.likes}</div>
					{:else}
						<HeartIcon class={largeBook ? 'h-8' : 'h-6'} />
					{/if}
				</button>
			{/if}
		{/if}
	</div>
</div>
{/if}

{#if menuPopup}
	<button
		in:fade|global={{ duration: 150, easing: circOut }}
		out:fade|global={{ duration: 150, easing: circOut }}
		on:click|stopPropagation|preventDefault={() => (menuPopup = false)}
		class="fixed z-40 h-screen w-screen min-w-screen min-h-screen top-0 -left-2 backdrop-blur-md"
	/>
	<menu-window
		use:popperContent={extraOpts}
		on:outclick={() => (menuPopup = false)}
		use:clickOutside
		class="select-none"
	>
		{#if userData.isBookmarked($bookmarks, book.uuid)}
			<button class="menu-item" on:click|preventDefault={() => handleMenuItem('remove')}>
				Remove Bookmark
			</button>
		{:else}
			<button class="menu-item" on:click|preventDefault={() => handleMenuItem('save')}>
				Save Bookmark
			</button>
		{/if}
		<button class="menu-item" on:click|preventDefault={() => handleMenuItem('addToJournal')}>
			Add to Journal
		</button>
		{#if showRemove}
			<button class="menu-item" on:click|preventDefault={() => handleMenuItem('removeFromJournal')}>
				Remove From Journal
			</button>
		{/if}
	</menu-window>
{/if}
